/**
 * Smooth Sales postcode shipping estimator (blocks/postcode-estimator.liquid).
 *
 * Posts the same form-encoded payload the ShipZip app's own widget sends to
 * its public rates endpoint and renders the returned services. Product data
 * (grams, price, sku, …) comes from data attributes that Horizon re-renders
 * on variant change, and is read at submit time so it is never stale.
 */

const ENDPOINT = 'https://sbz.cirkleinc.com/front-widget';

function formatMoney(cents) {
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

class PostcodeEstimator extends HTMLElement {
  connectedCallback() {
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.querySelector('form')?.removeEventListener('submit', this.#onSubmit);
  }

  #onSubmit = async (event) => {
    event.preventDefault();

    const input = this.querySelector('[name="postal_code"]');
    const button = this.querySelector('button[type="submit"]');
    const error = this.querySelector('.postcode-estimator__error');
    const list = this.querySelector('.postcode-estimator__rates');
    if (!input || !error || !list) return;

    error.hidden = true;
    list.hidden = true;

    const postcode = input.value.trim();
    if (postcode === '') {
      error.textContent = 'Please enter a postcode.';
      error.hidden = false;
      return;
    }

    /* Bracketed keys: the endpoint expects the nested form encoding the
     * app's jQuery widget produces, not JSON. */
    const params = new URLSearchParams();
    params.set('rate[destination][postal_code]', postcode);
    params.set('rate[destination][province]', '');
    params.set('rate[destination][country]', '');
    params.set('rate[items][0][quantity]', '1');
    params.set('rate[items][0][grams]', this.dataset.grams || '0');
    params.set('rate[items][0][price]', this.dataset.price || '0');
    params.set('rate[items][0][vendor]', this.dataset.vendor || '');
    params.set('rate[items][0][product_id]', this.dataset.productId || '');
    params.set('rate[items][0][sku]', this.dataset.sku || '');
    params.set('shop', this.dataset.shop || (window.Shopify && window.Shopify.shop) || '');

    if (button) button.disabled = true;
    this.setAttribute('aria-busy', 'true');

    let rates = null;
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        body: params,
        headers: { Accept: 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data && Array.isArray(data.rates)) rates = data.rates;
      }
    } catch (fetchError) {
      /* handled below as "no rates" */
    }

    if (button) button.disabled = false;
    this.removeAttribute('aria-busy');

    if (!rates || rates.length === 0) {
      error.replaceChildren(
        'Sorry! Shipping is not available for this location. Please ',
        Object.assign(document.createElement('a'), {
          href: this.dataset.contactUrl || '/pages/contact-us',
          textContent: 'contact us',
        }),
        '.'
      );
      error.hidden = false;
      return;
    }

    list.replaceChildren(
      ...rates.map((rate) => {
        const item = document.createElement('li');
        item.textContent = `${String(rate.service_name || '').trim()} - ${formatMoney(rate.total_price)}`;
        return item;
      })
    );
    list.hidden = false;
  };
}

if (!customElements.get('postcode-estimator')) {
  customElements.define('postcode-estimator', PostcodeEstimator);
}
